import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useState } from 'react';

import type {
  Rarity,
  RevealPropertyCurrency,
} from '@sorare/core/src/__generated__/globalTypes';
import { RevealableProperty } from '@sorare/core/src/__generated__/globalTypes';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';

import { ClueCta } from './ClueCta/index.tsx';
import { CluesPickerConfirmDialog } from './CluesPickerConfirmDialog/index.tsx';
import type { CluesPicker_cardPull } from './__generated__/index.graphql.ts';

type Props = {
  onRevealProperty: (
    property: RevealableProperty,
    spending?: RevealPropertyCurrency
  ) => Promise<void>;
  cardPull: CluesPicker_cardPull;
  rarity: Rarity;
  clues: RevealableProperty[];
  onSuccess?: () => void;
};

type RevealCost = CluesPicker_cardPull['pullConfig']['revealCosts'][number];

export const CluesPicker = ({
  onRevealProperty,
  cardPull,
  rarity,
  clues,
  onSuccess,
}: Props) => {
  const { currentUser } = useCurrentUserContext();

  const { craftClues } = currentUser ?? {};

  const [confirmDialogState, setConfirmDialogState] = useState<{
    selectedCost?: RevealCost;
  }>({
    selectedCost: undefined,
  });

  const { selectedCost } = confirmDialogState;

  const revealCosts = cardPull.pullConfig?.revealCosts.filter(rc =>
    clues.includesValue(rc.propertyName)
  );

  const selectedRevealCost = revealCosts?.find(
    cost => cost.propertyName === selectedCost?.propertyName
  );

  const isUsed = (property: RevealableProperty) => {
    switch (property) {
      case RevealableProperty.BEST_STAR_RANK:
        return (
          cardPull.bestQuality !== null || cardPull.bestGameplayTier !== null
        );
      case RevealableProperty.RESHUFFLE:
        return cardPull.reshuffled;
      case RevealableProperty.COUNTRY:
        return cardPull.activePulledCards?.some(card => !!card.country?.slug);
      case RevealableProperty.COMPETITION:
        return cardPull.activePulledCards?.some(
          card => !!card.competition?.slug
        );
      case RevealableProperty.HALLOWEEN_FIFTY_FIFTY:
      case RevealableProperty.FIFTY_FIFTY:
        return (
          cardPull.activePulledCards?.some(card => card.fiftyFiftySelected) &&
          cardPull.activePulledCards?.some(card => !card.fiftyFiftySelected)
        );
      case RevealableProperty.POSITION:
        return cardPull.activePulledCards?.some(card => !!card.position);
      case RevealableProperty.DIVISION:
        return cardPull.activePulledCards?.some(card => !!card.division?.name);
      default:
        return false;
    }
  };

  const isDisabled = (property: RevealableProperty) => {
    if (cardPull.reshuffled) {
      return true;
    }
    return isUsed(property);
  };

  const onCloseConfirmDialog = () => {
    setConfirmDialogState(prev => ({ ...prev, selectedCost: undefined }));
  };

  return (
    <>
      {revealCosts?.map(cost => {
        const correspondingCraftClue = craftClues?.find(
          clue => clue.currency === cost.currency
        );
        return (
          <ClueCta
            key={cost.propertyName}
            revealCost={cost}
            cluesAmount={correspondingCraftClue?.amount ?? 0}
            onClick={() =>
              setConfirmDialogState({
                selectedCost: cost,
              })
            }
            used={isUsed(cost.propertyName)}
            disabled={isDisabled(cost.propertyName)}
            rarity={rarity}
          />
        );
      })}
      <CluesPickerConfirmDialog
        key={selectedRevealCost?.propertyName}
        revealCost={selectedRevealCost}
        rarity={rarity}
        onClose={onCloseConfirmDialog}
        onConfirm={async spending => {
          if (!selectedRevealCost?.propertyName) {
            return;
          }
          await onRevealProperty(selectedRevealCost.propertyName, spending);
          onCloseConfirmDialog();
          onSuccess?.();
        }}
      />
    </>
  );
};

CluesPicker.fragments = {
  cardPull: gql`
    fragment CluesPicker_cardPull on CardPull {
      id
      bestQuality
      bestGameplayTier
      reshuffled
      activePulledCards {
        id
        country {
          slug
        }
        competition {
          slug
        }
        position
        division {
          name
        }
        fiftyFiftySelected
      }
      pullConfig {
        revealCosts {
          currency
          propertyName
          amountInCurrency
          amountInShards
        }
      }
    }
  ` as TypedDocumentNode<CluesPicker_cardPull>,
};
